import React, { useState, useEffect, useImperativeHandle, useCallback } from 'react';

import Box from '@mui/material/Box';

import SignatureField from './Signature.jsx';
import Checkbox from './Checkbox.jsx';
import DatePicker from './DatePicker.jsx';
import RadioGroup from './RadioGroup.jsx';
import { generateExceptionWithDataPreparation } from '../util/exceptionGenerator.js';

const Exception = React.forwardRef(({
	generatedDocument,
	name,
	disabled,
	onChange,
	exceptionType = 'independence-exception',
	fileName = 'Independence Exception Request',
	addPendingAction = () => { },
	removePendingAction = () => { },
	error: initialError,
	fields = [], // Array of field configurations with valueKey
	values = {}, // Entire form values object from parent
}, ref) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(initialError);
	
	// Internal form state - managed by the Exception component
	// Initialize with parent values if they exist (for data persistence when going back)
	const [internalFormData, setInternalFormData] = useState(() => {
		const initialData = {};
		fields.forEach(field => {
			// Use valueKey to get the value from the parent values object
			const parentValue = field.valueKey ? values[field.valueKey] : null;
			initialData[field.name] = parentValue !== undefined ? parentValue :
				(field.type === 'checkbox' ? false :
					field.type === 'date' ? null : '');
		});
		return initialData;
	});

	useEffect(() => {
		setError(initialError);
	}, [initialError]);

	// Update internal form data when values change (for data persistence)
	useEffect(() => {
		setInternalFormData(prev => {
			const updatedData = { ...prev };
			fields.forEach(field => {
				if (field.valueKey && values[field.valueKey] !== undefined) {
					updatedData[field.name] = values[field.valueKey];
				}
			});
			return updatedData;
		});
	}, [values, fields]);

	// Check if all required fields are filled
	const areRequiredFieldsFilled = fields.every(field => {
		if (!field.required) return true;
		
		const fieldValue = internalFormData[field.name];
		if (field.type === 'checkbox') {
			return fieldValue === true;
		}
		if (field.type === 'date') {
			return fieldValue !== null && fieldValue !== '';
		}
		if (field.type === 'signature') {
			return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
		}
		if (field.type === 'radio') {
			return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
		}
		return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
	});

	// Check if document already exists (for duplication prevention)
	const documentExists = !!generatedDocument;

	// Comprehensive error handler for any error in the component
	const handleError = useCallback((error, context = '') => {
		console.error(`Exception Component - Error in ${context}:`, error);
		
		// Set error state for inline display
		const errorMessage = error?.message || error?.toString() || 'An unexpected error occurred';
		setError(errorMessage);
		
		// Show a toast/snackbar if available
		if (window?.showToast) {
			window.showToast(errorMessage, { type: 'error' });
		}
		
		// Notify parent of failure
		onChange({ target: { name, value: null, error } });
		
		// Clear loading state
		setLoading(false);
	}, [name, onChange]);

	const handleFieldChange = (fieldName, value) => {
		try {
			setInternalFormData(prev => ({ ...prev, [fieldName]: value }));
			
			// Also update parent form values for compatibility
			onChange({ target: { name: fieldName, value } });
		} catch (error) {
			handleError(error, 'field change');
		}
	};

	const renderField = (field) => {
		try {
			const fieldValue = internalFormData[field.name];
			
			const fieldStyle = {
				marginBottom: '16px',
				marginTop: '8px',
			};
			
			switch (field.type) {
			case 'checkbox':
				return (
					<div key={field.name} style={fieldStyle}>
						<Checkbox
							name={field.name}
							value={fieldValue}
							label={field.label}
							onChange={(e) => handleFieldChange(field.name, e.target.value)}
							disabled={disabled || loading}
						/>
					</div>
				);
			case 'signature':
				return (
					<div key={field.name} style={fieldStyle}>
						<SignatureField
							name={field.name}
							value={fieldValue}
							label={field.label}
							onChange={(e) => handleFieldChange(field.name, e.target.value)}
							disabled={disabled || loading}
						/>
					</div>
				);
			case 'date':
				return (
					<div key={field.name} style={fieldStyle}>
						<DatePicker
							name={field.name}
							value={fieldValue}
							label={field.label}
							onChange={(e) => handleFieldChange(field.name, e.target.value)}
							disabled={disabled || loading}
							clearable={field.clearable !== false}
							disablePast={field.disablePast !== false}
							minDate={field.minDate}
							maxDate={field.maxDate}
						/>
					</div>
				);
			case 'radio':
				return (
					<div key={field.name} style={fieldStyle}>
						<RadioGroup
							name={field.name}
							value={fieldValue}
							label={field.label}
							options={field.options || []}
							onChange={(e) => handleFieldChange(field.name, e.target.value)}
							disabled={disabled || loading}
						/>
					</div>
				);
			default:
				return null;
			}
		} catch (error) {
			handleError(error, 'field rendering');
			return (
				<Box sx={{ color: 'error.main', p: 1 }}>
					Error rendering field: {field.name}
				</Box>
			);
		}
	};

	const handleGenerate = useCallback(async () => {
		if (loading || disabled) return;

		setError(null);
		setLoading(true);

		const action = crypto.randomUUID();
		try {
			addPendingAction(action);

			// Prepare data with internal form data and all external data from values
			const dataWithInternalFields = {
				...values, // All external data from parent (VAT, group, user data, etc.)
				...internalFormData, // All internal form data (checkbox, signature, dates, etc.)
			};
			
			// Use the exception generator utility with proper data preparation
			const generatedException = await generateExceptionWithDataPreparation(
				exceptionType,
				dataWithInternalFields,
				{
					fileName,
					addPendingAction,
					removePendingAction,
				},
			);
			
			removePendingAction(action);
			onChange({ target: { name, value: generatedException } });
			return generatedException;
		} catch (err) {
			handleError(err, 'PDF generation');
		} finally {
			removePendingAction(action);
			setLoading(false);
		}
	}, [loading, disabled, values, internalFormData, exceptionType, fileName, addPendingAction, removePendingAction, name, onChange, handleError]);

	const generate = useCallback(async () => {
		try {
			if (documentExists) {
				return generatedDocument;
			}
			
			if (!areRequiredFieldsFilled) {
				throw new Error('Required fields are not filled');
			}
			
			const uploadedDocument = await handleGenerate();
			
			if (uploadedDocument) {
				const updatedDocuments = [...(values.userDocuments || []), {
					...uploadedDocument,
					uploadedAt: new Date().toISOString(),
				}];
				
				onChange({
					target: { name: 'userDocuments', value: updatedDocuments },
				});
			}
			
			return uploadedDocument;
		} catch (error) {
			handleError(error, 'generate');
			throw error;
		}
	}, [areRequiredFieldsFilled, handleGenerate, documentExists, generatedDocument, values, onChange, handleError]);

	useImperativeHandle(ref, () => {
		return {
			generate,
			areRequiredFieldsFilled,
			loading,
			error,
		};
	}, [generate, areRequiredFieldsFilled, loading, error]);

	return (
		<Box sx={{ marginBottom: 2 }}>
			{/* Render fields based on configuration */}
			{fields.map(field => renderField(field))}
			{!!error && (
				<Box 
					sx={{ 
						color: 'text.primary', 
						mt: 1, 
						fontSize: 14,
						border: '1px solid',
						borderColor: 'error.main',
						borderRadius: 1,
						padding: 1.5,
						backgroundColor: 'error.light',
						'&::before': {
							content: '"⚠️ "',
							marginRight: 0.5,
						},
					}}
				>
					{error}
				</Box>
			)}
		</Box>
	);
});

Exception.type = 'Exception';

export default Exception; 